import { unByKey } from 'ol/Observable';
import Point from 'ol/geom/Point';
import { makeWMSLayer,makeVectorLayer,getStyle } from '@/js/layers';
import {
    wktFormat, geojsonFormat,
    isInSelectedMuncipality,
    addToolTipBase,
    toolTipIsVisible,
    clearToolTipBase,
    getWorkingAreaRestrictedMsg,
} from './tooltip';
import axios from '@/axios';
import { showSuccess,showInfo,showError,showLoading,hideLoading } from '../ui-helpers';
import { bus } from '@/main';
import teraiGeoJSON from '@/assets/geojson/terai.json';
import {postWithAuthToken} from '../../axios';

import store from '@/store';

const mapEventKeys = {};

const toolState = {
    isActive: false,
    map: null,
};

const teraiFeatures = geojsonFormat.readFeatures(JSON.stringify(teraiGeoJSON));
const isInTerai = coordinate => teraiFeatures.some(ftr => ftr.getGeometry().intersectsCoordinate(coordinate));

const mhpVectorStyles = {
    aoi: {
        geom: 'Polygon',
        fill: {color: '#fff3'},
        stroke: {color: '#0093d4', width: 2}
    },
    upstream: {
        geom: 'Line',
        stroke: {color: '#0093d4', width: 2.8}
    },
    intake: {
        geom: 'Point',
        stroke: {color: '#0083f4'},
        fill: {color: '#0083f4'},
        radius: 5,
        text: {text: 'Intake'}
    },
    powerhouse: feature =>
    {
        return getStyle({
            geom: 'Point',
            stroke: { color: '#009304',},
            fill: { color: '#009304',},
            radius: 5,
            text: { text: `${parseFloat(feature.get('power_kW'))} kW`, }
        });
    },
};

const mhpVectorLayers = Object.entries(mhpVectorStyles).reduce( (acc, [name, style]) =>
{
    acc[name] = makeVectorLayer({
        id: 'aoi',
        type: 'Vector',
        visible: true,
        zIndex: 3,
        vector_params: { url: null, style }
    });
    return acc;
}, {});

export const addFeatures = (combinedGeoJSON) =>
{
    Object.values(mhpVectorLayers).forEach(layer => layer.getSource().clear());
    console.assert(Object.keys(combinedGeoJSON).every(key => ["intake","powerhouse","aoi","upstream"].includes(key)), `Invalid key in: ${Object.keys(combinedGeoJSON)}`);
    Object.entries(combinedGeoJSON).forEach(([name, geojson]) =>
    {
        mhpVectorLayers[name].getSource().addFeatures(geojsonFormat.readFeatures(JSON.stringify(geojson)));
    });
}

const streamlineLayer = makeWMSLayer({
    type: "WMS",
    id: "streamline",
    visible: false,
    zIndex: 3,
    wms_params: {
        url: `${process.env.VUE_APP_GEOSERVER_BASE_URL}geoserver/MEP/wms`,
        source_version: "1.1.0",
        source_layers: "MEP:streamlineorderzwgs84",
        // cross_origin: 'anonymous',
    },
});


const addToolTip = (map, msg, {showOnly=false}={}) =>
{
    if (showOnly)
    {
        addToolTipBase(null,null,{showOnly})
        return;
    }
    const getMessage = coordinate =>
    {
        if (isInTerai(coordinate)) return 'MHP tool isn\'t meant for Terai region';
        return getWorkingAreaRestrictedMsg(coordinate, msg);
    };
    const hoverEventKey = addToolTipBase(map, getMessage);
    mapEventKeys.hover = hoverEventKey;
}

const clearToolTip = ({hideOnly=false}={}) =>
{
    clearToolTipBase({hideOnly});
    if (hideOnly) return;
    unByKey(mapEventKeys.hover);
}

const tooltipMsg = `
<ul style="margin-left:1.5em;">
    <li>Click at a point on a stream where you want to place the intake</li>
    <li>Press 'Shift' to hide/show this tooltip.</li>
    <li>Press 'Esc' to cancel.</li>
</ul>`;

const getDischargeAt = async ( point ) =>
{
   try {
      console.log("Querying discharge at", point);
      const res = await postWithAuthToken('api/bat/discharge-query/', { point });
      console.log("Discharge", res.data);
      if (res.data == 0)
         showInfo("Discharge not found.", "Please enter it manually");
      else
         showSuccess("Successfully loaded the discharge");
      return res.data;
   }
   catch (error) {
      console.error(error);
      showError("Error loading discharge")
      return 0;
   }
};

export const selectMhpTool = () =>
{
    toolState.isActive = true;
    const {map} = toolState;
    addToolTip(map, tooltipMsg);
    const clickEventKey = map.on('singleclick',event =>
    {
        if (!toolState.isActive || !isInSelectedMuncipality(event.coordinate) || isInTerai(event.coordinate)) return;
        const url = streamlineLayer.getSource().getFeatureInfoUrl(
          event.coordinate,
          map.getView().getResolution(),
          'EPSG:4326',
          {'INFO_FORMAT': 'application/json'}
        );
        console.assert(url, 'Didn\'t get any url from streamline wms source')
        if (!url) return;
        showLoading();
        axios.get(url)
        .then(({data: geojson}) =>
        {
            const streamFeatures = geojsonFormat.readFeatures(geojson);
            if (streamFeatures.length)
            {
                const arc_id = streamFeatures[0].get('arcid');
                const dam_pt_approx = wktFormat.writeGeometry(new Point(event.coordinate));
                getDischargeAt(event.coordinate).then((discharge) =>
                    bus.$emit('stream-selected', {arc_id, dam_pt_approx, discharge})
                );
            }
            else
            {
               bus.$emit('mhp-draw-canceled');
               showError("MHP Tool", "No stream selected! Please click on a stream")
            }
            hideLoading();
        })
        .catch((e) =>
        {
            showError("MHP Tool", e);
            bus.$emit('mhp-draw-canceled');
            hideLoading();
        });

        resetTool();
    });
    mapEventKeys.click = clickEventKey;
}

export const handleToolModeSelection = async (isSelected) =>
{
    if (isSelected)
    {
        setAllVectorLayersVisible(true);
        streamlineLayer.setVisible(true)
        store.dispatch('layers/setLayerVisibleWithUI', {layer_id: 'discharge', visible: true});
    }
    else
    {
        streamlineLayer.setVisible(false);
        setAllVectorLayersVisible(false);
        store.dispatch('layers/setLayerVisibleWithUI', {layer_id: 'discharge', visible: false});
        resetTool();
    }
};

const setAllVectorLayersVisible = (visible) =>
{
    Object.values(mhpVectorLayers).forEach( layer => layer.setVisible(visible) );
};

export const resetTool = () =>
{
    toolState.isActive = false;
    clearToolTip();
    Object.values(mapEventKeys).forEach(unByKey);
};

export const setupMhpTool = (map) =>
{
    map.addLayer(streamlineLayer);
    Object.values(mhpVectorLayers).forEach( layer => map.addLayer(layer) );
    toolState.map = map;
    window.addEventListener('keydown', event =>
    {
        if (toolState.isActive)
        {
            switch (event.key)
            {
            case "Escape":
                resetTool();
                bus.$emit('mhp-draw-canceled');
                break;
            case 'Shift':
                toolTipIsVisible() ? 
                    clearToolTip({hideOnly: true})
                    :
                    addToolTip(null,null,{showOnly: true});
                break;
            }
        }
    });
};
